import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavbarChevronIcon from '../layout/NavbarChevronIcon';
import DetailNavbarLeft from '../shared/DetailNavbarLeft';
import NavbarCloseButton from '../shared/NavbarCloseButton';
import {
  findSiblingIndex,
  parseOrderDetailNavState,
} from '../orders/orderdetail/orderDetailNavState';

interface InventoryTransferNavbarProps {
  readonly transferNumber: string;
  readonly transferName: string;
  readonly routeLabel: string;
}

function InventoryTransferNavbar({
  transferNumber,
  transferName,
  routeLabel,
}: InventoryTransferNavbarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { transferId } = useParams();
  const navState = parseOrderDetailNavState(location.state);

  const siblingIndex = findSiblingIndex(navState.siblingOrderIds, transferId);
  const hasSiblings = navState.siblingOrderIds.length > 1 && siblingIndex >= 0;
  const prevSiblingId =
    hasSiblings && siblingIndex > 0 ? navState.siblingOrderIds[siblingIndex - 1] : null;
  const nextSiblingId =
    hasSiblings && siblingIndex < navState.siblingOrderIds.length - 1
      ? navState.siblingOrderIds[siblingIndex + 1]
      : null;

  const goToSibling = (siblingId: string | number): void => {
    navigate(`/inventory-transfers/${siblingId}`, { replace: true, state: navState });
  };

  const title = transferNumber && transferName
    ? `#${transferNumber} ${transferName}`
    : transferName || transferNumber || 'Inventory Transfer';

  return (
    <nav className="order-detail-navbar" aria-label="Inventory transfer">
      <DetailNavbarLeft />
      <div className="order-detail-navbar-titles">
        <div className="order-detail-navbar-order-number">{title}</div>
        <div className="order-detail-navbar-order-name">{routeLabel}</div>
      </div>
      <div className="order-detail-navbar-right">
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => prevSiblingId != null && goToSibling(prevSiblingId)}
          disabled={prevSiblingId == null}
          aria-label="Previous transfer"
        >
          <NavbarChevronIcon direction="left" />
        </button>
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => nextSiblingId != null && goToSibling(nextSiblingId)}
          disabled={nextSiblingId == null}
          aria-label="Next transfer"
        >
          <NavbarChevronIcon direction="right" />
        </button>
        <NavbarCloseButton onClose={() => navigate('/inventory-transfers')} />
      </div>
    </nav>
  );
}

export default InventoryTransferNavbar;
